import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getSidebarCollapseOverride,
  isFullScreenChartRoute,
  subscribeSidebarCollapse,
} from "../../utils/sidebarCollapse";

function PageContainer({ children, gamePage }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const location = useLocation();
  const hiddenSidebarRoutes = [
    '/',
    '/auth/register',
    '/auth/login',
    '/auth/verification',
    '/auth/forgot-password'
  ];

  const shouldHideSidebar =
    hiddenSidebarRoutes.includes(location.pathname) ||
    location.pathname.startsWith('/auth/register/') ||
    // The reset-password screen carries a token in the path, so it can never
    // match the exact list above. Without this it reserved the 256px sidebar
    // gutter and pushed its split layout off-centre, unlike every other auth
    // screen. Sidebar.jsx and AuthLayout.jsx already special-case it the same way.
    location.pathname.startsWith('/reset-password/');


  useEffect(() => {
    const computeCollapsed = () => {
      // Includes /<slug>/trade — see isFullScreenChartRoute.
      if (!isFullScreenChartRoute(location.pathname)) return false;
      const override = getSidebarCollapseOverride();
      return override === null ? false : override;
    };
    setIsCollapsed(computeCollapsed());
    const unsubscribe = subscribeSidebarCollapse(() => {
      setIsCollapsed(computeCollapsed());
    });
    return unsubscribe;
  }, [location]);

  const isMobileView = document.body.clientWidth <= 768;
  const isChartsPath =
    location.pathname.endsWith("/charts") ||
    location.pathname.startsWith("/chartSearch/");

  // COLLAPSED is keyed on isFullScreenChartRoute (which also matches
  // /<slug>/trade); EXPANDED stays keyed on isChartsPath.
  //
  // Why the two differ. `isChartsCollapsedDesktop` is what drops the ml-64
  // sidebar gutter and the horizontal padding, so without the League Trade
  // screen in it the page kept reserving the sidebar's 256px after the sidebar
  // had already been hidden — pressing Full Screen there hid the chrome and then
  // left the chart pinned in its old column with a dead grey band beside it, and
  // the canvas (drawn at the full 1209px) overflowing its 936px container on the
  // right. Measured at 1536x900: /charts collapsed puts the chart column at
  // left 40 / width 1208; /<slug>/trade collapsed was left 304 / width 936.
  //
  // `isChartsExpandedDesktop` is deliberately NOT widened. It only picks between
  // two paddings (pl-0 pr-2 vs px-2) and the drop shadow — cosmetic differences
  // the client has not asked about — and the League Trade screen looks the way it
  // looks today in its normal, uncollapsed state. Nothing there moves unless the
  // user presses Full Screen.
  const isChartsCollapsedDesktop =
    isFullScreenChartRoute(location.pathname) && isCollapsed && !isMobileView;
  const isChartsExpandedDesktop = isChartsPath && !isCollapsed && !isMobileView;

  // Sidebar is always w-64 when visible; ml-0 when collapsed on charts
  const sideBarWidth = shouldHideSidebar
    ? ""
    : isChartsCollapsedDesktop
    ? "md:ml-0"
    : "md:ml-64";
  const isTradingFloor = location.pathname === "/trading-floor";

  return (
    <div
      className={`${shouldHideSidebar ? "" : isTradingFloor ? "px-0" : isChartsCollapsedDesktop ? "px-0" : isChartsExpandedDesktop ? "pl-0 pr-2" : "px-2"} ${
        isChartsPath && isMobileView && "!pt-0"
      } ${gamePage ? "game_page_with_sidebar max-md:!block" : "block"} ${
        isMobileView
          ? ` ${sideBarWidth} bg-white`
          : isChartsCollapsedDesktop
          ? ` ${sideBarWidth} bg-white`
          : isChartsExpandedDesktop
          ? ` ${sideBarWidth} bg-white`
          : ` ${sideBarWidth} shadow-md bg-white`
      }`}
    >
      {children}
    </div>
  );
}

export default PageContainer;
